import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Briefcase, MapPin, Clock, CheckCircle, Upload } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_BRAND = {
  name: 'Recruitizr',
  tagline: 'Find your next opportunity.',
  logo_url: '',
  primary_color: '#1e3a8a',
};

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  const days = Math.floor(diff / 86400);
  return days <= 30 ? `${days}d ago` : d.toLocaleDateString();
}

function JobPageHeader({ brand, slug }) {
  return (
    <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
      <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
        <Link to={`/careers/${slug}`} className="flex items-center gap-3" data-testid="jp-brand-link">
          {brand.logo_url ? (
            <img src={brand.logo_url} alt={`${brand.name} Logo`} className="h-12 w-12 object-contain" />
          ) : (
            <div className="h-12 w-12 rounded-lg flex items-center justify-center text-white font-extrabold text-xl" style={{ backgroundColor: brand.primary_color }}>
              {brand.name.charAt(0)}
            </div>
          )}
          <span className="text-xl font-extrabold" style={{ fontFamily: 'Manrope, sans-serif', color: brand.primary_color }}>
            Careers @ {brand.name}
          </span>
        </Link>
        <Link to={`/careers/${slug}`} data-testid="jp-back-link"
              className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 font-bold">
          <ArrowLeft size={16} /> All Jobs
        </Link>
      </div>
    </header>
  );
}

/**
 * Per-job public page for SEO + Google Jobs + Indeed.
 * URL: /careers/:slug/:jobSlug — where jobSlug is "{slugified-title}-{short8}".
 * Minimal layout per product decision: title + JD + Apply button only.
 */
export default function JobPostingPage() {
  const { slug, jobSlug } = useParams();
  const navigate = useNavigate();
  const [brand, setBrand] = useState(DEFAULT_BRAND);
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Apply form state
  const [applyOpen, setApplyOpen] = useState(false);
  const [applied, setApplied] = useState(false);
  const [form, setForm] = useState({ candidate_name: '', email: '', phone: '', cover_letter: '' });
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [tRes, jRes] = await Promise.all([
          axios.get(`${API}/public/tenants/${slug}`).catch(() => null),
          axios.get(`${API}/public/careers/${slug}/${jobSlug}`),
        ]);
        if (cancelled) return;
        if (tRes?.data) {
          setBrand({
            name: tRes.data.name || DEFAULT_BRAND.name,
            tagline: tRes.data.tagline || `Join ${tRes.data.name}`,
            logo_url: tRes.data.logo_url || '',
            primary_color: tRes.data.primary_color || DEFAULT_BRAND.primary_color,
          });
          if (tRes.data.favicon_url) {
            document.querySelectorAll(
              "link[rel='icon'], link[rel='shortcut icon'], link[rel='apple-touch-icon'], link[rel='mask-icon']"
            ).forEach(n => n.parentNode && n.parentNode.removeChild(n));
            const link = document.createElement('link');
            link.rel = 'icon';
            link.href = tRes.data.favicon_url;
            document.head.appendChild(link);
          }
        }
        setJob(jRes.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || 'Job not found');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, jobSlug]);

  // SEO: <title>, meta description, AND JSON-LD JobPosting schema
  useEffect(() => {
    if (!job) return;
    const prevTitle = document.title;
    document.title = `${job.title} — Careers @ ${brand.name}`;

    // meta description
    let meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute('content') : null;
    if (!meta) {
      meta = document.createElement('meta');
      meta.setAttribute('name', 'description');
      document.head.appendChild(meta);
    }
    const descPreview = (job.description || job.requirements || '').replace(/\s+/g, ' ').trim().slice(0, 160);
    meta.setAttribute('content', descPreview || `Apply for ${job.title} at ${brand.name}.`);

    // JSON-LD JobPosting (Google for Jobs)
    const ld = {
      '@context': 'https://schema.org/',
      '@type': 'JobPosting',
      title: job.title,
      description: job.description || job.requirements || '',
      datePosted: job.created_at,
      hiringOrganization: {
        '@type': 'Organization',
        name: brand.name,
        sameAs: typeof window !== 'undefined' ? `${window.location.origin}/careers/${slug}` : undefined,
        logo: brand.logo_url || undefined,
      },
      employmentType: (job.employment_type || 'FULL_TIME').toUpperCase().replace(/[-\s]/g, '_'),
      jobLocation: job.location ? {
        '@type': 'Place',
        address: { '@type': 'PostalAddress', addressLocality: job.location },
      } : undefined,
      directApply: true,
      identifier: { '@type': 'PropertyValue', name: brand.name, value: job.id },
    };
    if (job.salary_min || job.salary_max) {
      ld.baseSalary = {
        '@type': 'MonetaryAmount',
        currency: job.salary_currency || 'INR',
        value: {
          '@type': 'QuantitativeValue',
          minValue: job.salary_min || undefined,
          maxValue: job.salary_max || undefined,
          unitText: 'YEAR',
        },
      };
    }
    const scriptEl = document.createElement('script');
    scriptEl.type = 'application/ld+json';
    scriptEl.id = 'recruitizr-jobposting-ld';
    scriptEl.text = JSON.stringify(ld);
    document.head.appendChild(scriptEl);

    return () => {
      document.title = prevTitle;
      if (meta && prevDesc !== null) meta.setAttribute('content', prevDesc);
      const s = document.getElementById('recruitizr-jobposting-ld');
      if (s && s.parentNode) s.parentNode.removeChild(s);
    };
  }, [job, brand, slug]);

  const handleApply = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const formData = new FormData();
      if (file) formData.append('file', file);
      const q = new URLSearchParams({
        candidate_name: form.candidate_name,
        email: form.email,
        phone: form.phone,
        cover_letter: form.cover_letter,
        tenant_slug: slug,
      }).toString();
      await axios.post(`${API}/public/jobs/${job.id}/apply?${q}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setApplied(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit application');
    } finally { setSubmitting(false); }
  };

  // ──────────────────────────────────────────────────────────────────────────
  // Render branches
  // ──────────────────────────────────────────────────────────────────────────
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500" data-testid="jp-loading">Loading…</div>;
  }
  if (error || !job) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="jp-error">
        <div className="text-center">
          <Briefcase size={64} className="mx-auto mb-4 text-slate-300" />
          <h1 className="text-xl font-bold text-slate-700 mb-2">{error || 'Job not found'}</h1>
          <Link to={`/careers/${slug}`} className="text-blue-600 hover:underline text-sm">← Back to all jobs</Link>
        </div>
      </div>
    );
  }

  const Header = <JobPageHeader brand={brand} slug={slug} />;

  // ── Apply form ───────────────────────────────────────────────────────────
  if (applyOpen) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {Header}
        <main className="max-w-2xl mx-auto px-6 py-10">
          {applied ? (
            <div className="text-center py-16" data-testid="jp-apply-success">
              <CheckCircle size={64} className="mx-auto mb-4 text-emerald-500" />
              <h2 className="text-2xl font-bold text-blue-900 mb-2">Application Submitted!</h2>
              <p className="text-slate-600 mb-6">
                Thank you for applying to <strong>{job.title}</strong>. We&apos;ll review your application and get back to you soon.
              </p>
              <button onClick={() => navigate(`/careers/${slug}`)} className="px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition-colors" data-testid="jp-browse-more-btn">
                Browse More Jobs
              </button>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-extrabold text-blue-900 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>Apply for {job.title}</h1>
              <p className="text-slate-500 mb-6">{job.location}</p>
              <form onSubmit={handleApply} className="space-y-5 bg-white border border-slate-200 rounded-lg p-6 shadow-sm" data-testid="jp-apply-form">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-blue-900 block mb-1.5">Full Name *</label>
                  <input data-testid="jp-apply-name" value={form.candidate_name} onChange={e => setForm({ ...form, candidate_name: e.target.value })} required
                    className="w-full h-11 rounded-lg border border-slate-300 px-4 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none" placeholder="Your full name" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-blue-900 block mb-1.5">Email *</label>
                    <input data-testid="jp-apply-email" type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required
                      className="w-full h-11 rounded-lg border border-slate-300 px-4 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none" placeholder="your@email.com" />
                  </div>
                  <div>
                    <label className="text-xs font-bold uppercase tracking-wider text-blue-900 block mb-1.5">Phone</label>
                    <input data-testid="jp-apply-phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                      className="w-full h-11 rounded-lg border border-slate-300 px-4 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none" placeholder="+91-XXXXXXXXXX" />
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-blue-900 block mb-1.5">Cover Letter</label>
                  <textarea data-testid="jp-apply-cover" value={form.cover_letter} onChange={e => setForm({ ...form, cover_letter: e.target.value })} rows={4}
                    className="w-full rounded-lg border border-slate-300 px-4 py-2 text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none resize-none" placeholder="Tell us why you're a great fit..." />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-blue-900 block mb-1.5">Resume / CV</label>
                  <label className="block border-2 border-dashed border-slate-300 rounded-lg px-4 py-6 cursor-pointer hover:bg-slate-50 transition-colors text-center">
                    <Upload size={20} className="mx-auto mb-1 text-slate-400" />
                    <span className="text-sm text-slate-500" data-testid="jp-apply-file-label">{file ? file.name : 'Click to upload (PDF/DOC, optional)'}</span>
                    <input data-testid="jp-apply-file" type="file" accept=".pdf,.doc,.docx" onChange={e => setFile(e.target.files?.[0] || null)} className="hidden" />
                  </label>
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => { setApplyOpen(false); }}
                          className="px-5 py-3 border border-slate-300 text-slate-700 font-bold rounded-lg hover:bg-slate-50" data-testid="jp-apply-cancel-btn">
                    Cancel
                  </button>
                  <button type="submit" disabled={submitting}
                          className="flex-1 px-5 py-3 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg disabled:opacity-50 transition-colors" data-testid="jp-apply-submit-btn">
                    {submitting ? 'Submitting…' : 'Submit Application'}
                  </button>
                </div>
              </form>
            </>
          )}
        </main>
      </div>
    );
  }

  // ── Minimal job page: title + JD + Apply button ───────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white" data-testid="jp-page">
      {Header}
      <main className="max-w-3xl mx-auto px-6 py-12">
        <h1 className="text-3xl sm:text-4xl font-extrabold text-blue-900 mb-3 leading-tight" data-testid="jp-title" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {job.title}
        </h1>
        <div className="flex flex-wrap gap-2 text-xs text-slate-500 mb-8">
          {job.location && (
            <span className="flex items-center gap-1.5 bg-blue-50 text-blue-700 px-3 py-1 rounded-full" data-testid="jp-location">
              <MapPin size={12} /> {job.location}
            </span>
          )}
          {job.created_at && (
            <span className="flex items-center gap-1.5 text-slate-500">
              <Clock size={12} /> Posted {timeAgo(job.created_at)}
            </span>
          )}
        </div>

        {(job.description || job.requirements) && (
          <section className="mb-10" data-testid="jp-jd">
            <p className="text-slate-700 leading-relaxed whitespace-pre-wrap text-[15px]">
              {job.description || job.requirements}
            </p>
          </section>
        )}

        <button
          data-testid="jp-apply-btn"
          onClick={() => setApplyOpen(true)}
          className="w-full sm:w-auto px-10 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg text-lg transition-colors shadow-sm"
        >
          Apply Now
        </button>
      </main>

      <footer className="border-t border-slate-200 py-8 text-center text-sm text-slate-500 mt-12">
        <p>&copy; {new Date().getFullYear()} {brand.name}. All rights reserved.</p>
      </footer>
    </div>
  );
}
